import { BasicKeyedStorageManager } from '@bridebook/toolbox/src/storage-managers/managers/basic-keyed-storage-manager';

type EngagementCounters = {
  searchCount: number;
  filtersApplied: boolean;
  profileViews: number;
  favourited: boolean;
};

const DEFAULTS: EngagementCounters = {
  searchCount: 0,
  filtersApplied: false,
  profileViews: 0,
  favourited: false,
};

const THRESHOLDS = {
  searchCount: 3,
  profileViews: 3,
};

/**
 * Toggle between 'localStorage' (persists across sessions) and
 * 'sessionStorage' (resets when tab closes).
 */
export type EngagementStorageMode = 'localStorage' | 'sessionStorage';

let storageMode: EngagementStorageMode = 'sessionStorage';

export const setEngagementStorageMode = (mode: EngagementStorageMode) => {
  storageMode = mode;
  cache.clear();
};

const STORAGE_KEY = 'compassEngagement';

const cache = new Map<string, EngagementCounters>();

const cacheKey = (userId: string, taskId: string) => `${userId}_${taskId}`;

const readFromStorage = (userId: string, taskId: string): EngagementCounters | null => {
  const id = cacheKey(userId, taskId);

  if (storageMode === 'localStorage') {
    return new BasicKeyedStorageManager<EngagementCounters>({
      storageKey: STORAGE_KEY,
      id,
    }).get();
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, EngagementCounters>;
    return parsed[id] ?? null;
  } catch {
    return null;
  }
};

const writeToStorage = (userId: string, taskId: string, counters: EngagementCounters) => {
  const id = cacheKey(userId, taskId);

  if (storageMode === 'localStorage') {
    new BasicKeyedStorageManager<EngagementCounters>({
      storageKey: STORAGE_KEY,
      id,
    }).set(counters);
    return;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const existing = raw ? (JSON.parse(raw) as Record<string, EngagementCounters>) : {};
    existing[id] = counters;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // sessionStorage full or unavailable
  }
};

const getCounters = (userId: string, taskId: string): EngagementCounters => {
  const key = cacheKey(userId, taskId);
  const cached = cache.get(key);
  if (cached) return cached;
  const stored = readFromStorage(userId, taskId);
  const counters = stored ?? { ...DEFAULTS };
  cache.set(key, counters);
  return counters;
};

const persist = (userId: string, taskId: string, counters: EngagementCounters) => {
  cache.set(cacheKey(userId, taskId), counters);
  writeToStorage(userId, taskId, counters);
};

export const isEngagementComplete = (userId: string, taskId: string): boolean => {
  const c = getCounters(userId, taskId);
  return (
    c.searchCount >= THRESHOLDS.searchCount ||
    c.filtersApplied ||
    c.profileViews >= THRESHOLDS.profileViews ||
    c.favourited
  );
};

export const incrementSearchCount = (userId: string, taskId: string) => {
  const c = {
    ...getCounters(userId, taskId),
    searchCount: getCounters(userId, taskId).searchCount + 1,
  };
  persist(userId, taskId, c);
};

export const markFiltersApplied = (userId: string, taskId: string) => {
  const c = { ...getCounters(userId, taskId), filtersApplied: true };
  persist(userId, taskId, c);
};

export const incrementProfileViews = (userId: string, taskId: string) => {
  const c = {
    ...getCounters(userId, taskId),
    profileViews: getCounters(userId, taskId).profileViews + 1,
  };
  persist(userId, taskId, c);
};

export const markFavourited = (userId: string, taskId: string) => {
  const c = { ...getCounters(userId, taskId), favourited: true };
  persist(userId, taskId, c);
};
